import EditOutlined from "@mui/icons-material/EditOutlined";
import PhoneAndroidOutlined from "@mui/icons-material/PhoneAndroidOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  useSendPhoneVerificationOtpMutation,
  useVerifyPhoneOtpMutation,
} from "api/api-auth";
import { memo, type ReactNode, useCallback, useEffect, useState } from "react";
import { parsePhoneNumber } from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import PhoneOtpEntry from "components/Common/PhoneOtpEntry";
import {
  getFriendlyPhoneOtpSendError,
  getFriendlyPhoneOtpVerificationError,
  isPhoneOtpCooldownError,
  normalizePhoneNumberForOtp,
} from "helpers/phoneVerification";
import { useGetAccountByIdQuery } from "hooks/useGetAccountById";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { toast } from "react-toastify";
import type { CurrentUser } from "types";

const PHONE_VERIFICATION_COUNTRIES = new Set([
  "SG",
  "SGP",
  "SINGAPORE",
  "IN",
  "IND",
  "INDIA",
  "GB",
  "GBR",
  "UK",
  "UNITED KINGDOM",
  "AU",
  "AUS",
  "AUSTRALIA",
]);

const dialogPaperProps = {
  sx: {
    width: "100%",
    maxWidth: "520px !important",
    borderRadius: "16px",
    border: "1px solid #E2E8F0",
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.18)",
  },
};
const dialogBackdropProps = {
  sx: {
    backgroundColor: "rgba(15, 23, 42, 0.4)",
    backdropFilter: "blur(4px)",
  },
};

const RESEND_COOLDOWN_MS = 60 * 1000;

type PhoneVerificationGateProps = {
  children: ReactNode;
  user: CurrentUser;
  onLogout: () => Promise<void>;
  refreshCurrentUser: () => Promise<CurrentUser>;
};

const PhoneVerificationGate = ({
  children,
  user,
  onLogout,
  refreshCurrentUser,
}: PhoneVerificationGateProps) => {
  const [sendPhoneCode, { isLoading: isSending }] =
    useSendPhoneVerificationOtpMutation();
  const [verifyPhone, { isLoading: isVerifying }] = useVerifyPhoneOtpMutation();
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codePhoneNumber, setCodePhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [errorPhoneNumber, setErrorPhoneNumber] = useState("");
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [isRefreshingUser, setIsRefreshingUser] = useState(false);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string | null>(null);
  const selectedAccountId = useAppSelector(
    (state) => state.appState.permission,
  );
  const { data: selectedAccount } = useGetAccountByIdQuery(selectedAccountId);
  const phoneNumber = user.mobile?.trim() ?? "";
  const normalizedPhoneNumber = normalizePhoneNumberForOtp(phoneNumber);
  const phoneIdentity = `${user.id}:${normalizedPhoneNumber}`;
  const [previousPhoneIdentity, setPreviousPhoneIdentity] = useState(phoneIdentity);
  // Reset before committing a new phone's UI, including when changing back to
  // a previously verified number. React batches these local state updates.
  if (previousPhoneIdentity !== phoneIdentity) {
    setPreviousPhoneIdentity(phoneIdentity);
    setVerifiedPhoneNumber(null);
    setCodeSent(false);
    setCodePhoneNumber("");
    setCode("");
    setResendAt(null);
  }
  const hasCodeForCurrentPhone =
    codeSent && codePhoneNumber === normalizedPhoneNumber;
  const currentPhoneError =
    errorPhoneNumber === normalizedPhoneNumber ? error : "";
  const parsedPhoneNumber = phoneNumber
    ? parsePhoneNumber(phoneNumber)
    : undefined;
  const isRequiredCountry = [
    user.country_iso3,
    user.country,
    parsedPhoneNumber?.country,
    selectedAccount?.country,
    ...(user.roles ?? []).flatMap(({ company }) => [
      company.country,
      company.country_iso3,
    ]),
  ].some((country) =>
    PHONE_VERIFICATION_COUNTRIES.has(
      String(country ?? "")
        .trim()
        .toUpperCase(),
    ),
  );
  const isRequired =
    isRequiredCountry &&
    user.phone_verified !== true &&
    verifiedPhoneNumber !== normalizedPhoneNumber;
  const hasPhone = Boolean(phoneNumber);
  const location = useLocation();
  const navigate = useNavigate();
  const phoneSettingsNavigationState = location.state as {
    phoneVerificationReturnTo?: string;
    phoneVerificationPhoneAtEditStart?: string;
  } | null;
  const phoneSettingsReturnTo =
    phoneSettingsNavigationState?.phoneVerificationReturnTo;
  const phoneAtEditStart =
    phoneSettingsNavigationState?.phoneVerificationPhoneAtEditStart;
  const isPhoneSettingsRoute =
    location.pathname.replace(/\/+$/, "") === "/settings" &&
    new URLSearchParams(location.search).get("phoneVerification") === "true";
  const openPhoneSettings = useCallback(() => {
    navigate("/settings?phoneVerification=true", {
      state: {
        phoneVerificationReturnTo: `${location.pathname}${location.search}`,
        phoneVerificationPhoneAtEditStart: normalizedPhoneNumber,
      },
    });
  }, [location.pathname, location.search, navigate, normalizedPhoneNumber]);

  useEffect(() => {
    if (
      isPhoneSettingsRoute &&
      phoneSettingsReturnTo &&
      phoneAtEditStart !== undefined &&
      user.mobile?.trim() &&
      normalizedPhoneNumber !== phoneAtEditStart
    ) {
      navigate(phoneSettingsReturnTo, { replace: true });
    }
  }, [
    isPhoneSettingsRoute,
    navigate,
    normalizedPhoneNumber,
    phoneAtEditStart,
    phoneSettingsReturnTo,
    user.mobile,
  ]);

  const requestCode = useCallback(
    async (phoneNumber: string) => {
      const normalizedPhone = normalizePhoneNumberForOtp(phoneNumber);
      if (!isValidPhoneNumber(normalizedPhone)) {
        setError("Enter a valid phone number with its country code, then try again.");
        setErrorPhoneNumber(normalizedPhone);
        return;
      }
      try {
        await sendPhoneCode({ phone_number: normalizedPhone }).unwrap();
        setCodeSent(true);
        setCodePhoneNumber(normalizedPhone);
        setCode("");
        setError("");
        setErrorPhoneNumber("");
        setResendAt(Date.now() + RESEND_COOLDOWN_MS);
      } catch (requestError) {
        if (isPhoneOtpCooldownError(requestError)) {
          // The backend keeps the previously issued challenge alive during the
          // resend cooldown. Let the user enter that code after a fresh login.
          setCodeSent(true);
          setCodePhoneNumber(normalizedPhone);
          setResendAt(Date.now() + RESEND_COOLDOWN_MS);
        }
        setError(getFriendlyPhoneOtpSendError(requestError));
        setErrorPhoneNumber(normalizedPhone);
      }
    },
    [sendPhoneCode],
  );

  const confirmCode = useCallback(async () => {
    const otpCode = code.replace(/\s/g, "");
    if (!/^\d{6}$/.test(otpCode)) {
      setError("Enter the 6-digit verification code.");
      setErrorPhoneNumber(normalizedPhoneNumber);
      return;
    }
    try {
      setIsRefreshingUser(true);
      await verifyPhone({ otp_code: otpCode }).unwrap();
      // The OTP endpoint is authoritative. Close the gate immediately while
      // refreshing the cached user so the verified flag stays in sync.
      setVerifiedPhoneNumber(normalizedPhoneNumber);
      setError("");
      setErrorPhoneNumber("");
      setCode("");
      setCodeSent(false);
      setCodePhoneNumber("");
      setResendAt(null);
      toast.success("Your phone number has been verified.");
      await refreshCurrentUser().catch(() => undefined);
    } catch (verificationError) {
      setError(getFriendlyPhoneOtpVerificationError(verificationError));
      setErrorPhoneNumber(normalizedPhoneNumber);
    } finally {
      setIsRefreshingUser(false);
    }
  }, [code, normalizedPhoneNumber, refreshCurrentUser, verifyPhone]);

  const isBusy = isSending || isVerifying || isRefreshingUser;
  const handleOtpCodeChange = useCallback(
    (nextCode: string) => {
      setCode(nextCode);
      setError("");
      setErrorPhoneNumber(normalizedPhoneNumber);
    },
    [normalizedPhoneNumber],
  );
  const handleResendCode = useCallback(
    () => requestCode(phoneNumber),
    [phoneNumber, requestCode],
  );
  const submit = useCallback(() => {
    if (hasCodeForCurrentPhone) return confirmCode();
    if (hasPhone) return requestCode(phoneNumber);
    return openPhoneSettings();
  }, [
    confirmCode,
    hasCodeForCurrentPhone,
    hasPhone,
    openPhoneSettings,
    phoneNumber,
    requestCode,
  ]);

  const shouldBlockApp = isRequired && !isPhoneSettingsRoute;
  const formattedPhone =
    parsedPhoneNumber?.formatInternational() ?? phoneNumber;
  const spokenPhone = phoneNumber.replace(/\D/g, " ").trim();

  return (
    <>
      <div
        ref={(element) => {
          if (element) element.inert = shouldBlockApp;
        }}
      >
        {children}
      </div>
      {shouldBlockApp && (
        <Dialog
          open
          onClose={() => undefined}
          disableEscapeKeyDown
          fullWidth
          maxWidth="sm"
          PaperProps={dialogPaperProps}
          BackdropProps={dialogBackdropProps}
          aria-labelledby="phone-verification-title"
          aria-describedby="phone-verification-description"
          aria-modal="true"
        >
          <DialogTitle sx={{ px: { xs: 3, sm: 4 }, pt: 4, pb: 0 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                aria-hidden="true"
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: "primary.main",
                  backgroundColor: "#EAF2FF",
                }}
              >
                <PhoneAndroidOutlined />
              </Box>
              <Typography
                id="phone-verification-title"
                component="h2"
                sx={{ fontSize: "1.375rem", lineHeight: 1.3, fontWeight: 600 }}
              >
                Verify your phone number
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
            <Stack spacing={2}>
              {!hasPhone ? (
                <DialogContentText
                  id="phone-verification-description"
                  sx={{ color: "#475569", fontWeight: 400 }}
                >
                  Add a phone number to secure your account and continue.
                </DialogContentText>
              ) : hasCodeForCurrentPhone ? (
                <>
                  <DialogContentText
                    id="phone-verification-description"
                    sx={{ color: "#475569", fontWeight: 400 }}
                  >
                    Enter the 6-digit code we texted to
                  </DialogContentText>
                  <Typography
                    sx={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color: "#0F172A",
                    }}
                    aria-label={`Phone number ${spokenPhone}`}
                  >
                    {formattedPhone}
                  </Typography>
                </>
              ) : (
                <>
                  <DialogContentText
                    id="phone-verification-description"
                    sx={{ color: "#475569", fontWeight: 400 }}
                  >
                    We’ll text a 6-digit code by SMS to
                  </DialogContentText>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      borderRadius: 1.5,
                      backgroundColor: "#F1F5F9",
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        color: "#0F172A",
                      }}
                      aria-label={`Phone number ${spokenPhone}`}
                    >
                      {formattedPhone}
                    </Typography>
                  </Box>
                  <DialogContentText sx={{ color: "#475569", fontWeight: 400 }}>
                    Verification helps secure your account.
                  </DialogContentText>
                </>
              )}
              {hasCodeForCurrentPhone && (
                <PhoneOtpEntry
                  value={code}
                  onChange={handleOtpCodeChange}
                  error={currentPhoneError}
                  disabled={isBusy}
                  resendAt={resendAt}
                  countdownActive={hasCodeForCurrentPhone}
                  isResending={isSending}
                  onResend={handleResendCode}
                />
              )}
              {currentPhoneError && !hasCodeForCurrentPhone && (
                <Typography color="error" role="alert" variant="body2">
                  {currentPhoneError}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 3, sm: 4 }, pt: 2, pb: 4 }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Button
                onClick={onLogout}
                disabled={isBusy}
                sx={{
                  order: { xs: 3, sm: 1 },
                  alignSelf: { xs: "flex-start", sm: "auto" },
                  minHeight: 44,
                  px: 1,
                  color: "#475569",
                  textTransform: "none",
                  fontWeight: 500,
                  "&.Mui-focusVisible": {
                    outline: "2px solid",
                    outlineOffset: 2,
                  },
                }}
              >
                Log out
              </Button>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{
                  order: { xs: 1, sm: 2 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <LoadingButton
                  variant="contained"
                  onClick={submit}
                  loading={isBusy}
                  autoFocus={!hasCodeForCurrentPhone}
                  disabled={
                    isBusy ||
                    (hasCodeForCurrentPhone &&
                      !/^\d{6}$/.test(code.replace(/\s/g, "")))
                  }
                  sx={{
                    minHeight: 44,
                    textTransform: "none",
                    fontWeight: 500,
                    "&.Mui-focusVisible": {
                      outline: "2px solid",
                      outlineOffset: 2,
                    },
                  }}
                >
                  {hasCodeForCurrentPhone
                    ? "Verify code"
                    : hasPhone
                      ? "Send verification code"
                      : "Add phone number"}
                </LoadingButton>
                {hasPhone && (
                  <Button
                    type="button"
                    onClick={openPhoneSettings}
                    disabled={isBusy}
                    startIcon={<EditOutlined />}
                    variant="outlined"
                    sx={{
                      minHeight: 44,
                      textTransform: "none",
                      fontWeight: 500,
                      "&.Mui-focusVisible": {
                        outline: "2px solid",
                        outlineOffset: 2,
                      },
                    }}
                  >
                    Update phone number
                  </Button>
                )}
              </Stack>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default memo(PhoneVerificationGate);
